import * as React from "react";
import { Form, Formik, getIn, type FormikProps } from "formik";
import * as Yup from "yup";
import Button from "@src/components/Button";
import Input from "@src/components/Input";
import PhoneNumberInput from "@src/components/InputPhone";
import invitation from "./invitation.module.scss";
import toast from "react-hot-toast";
import Image from "next/image";
import { useQuery, useMutation } from "@apollo/client";
import {
  Event_PreferencesQuery,
  Event_PreferencesQueryVariables,
  GenerateInvitationSelfiePreSignedUrlMutation,
  GenerateInvitationSelfiePreSignedUrlMutationVariables,
  InvitationCardQuery,
  InvitationCardQueryVariables,
  MyPlusOneRequestsQuery,
  MyPlusOneRequestsQueryVariables,
  PlusOneRequestType,
  RequestPlusOneMutation,
  RequestPlusOneMutationVariables,
  UpdateRsvpStatusMutation,
  UpdateRsvpStatusMutationVariables,
  VerifyInvitationSelfieUploadMutation,
  VerifyInvitationSelfieUploadMutationVariables,
} from "@src/@types/api.d";
import {
  GetEventPreferences,
  InvitationCard,
  MY_PLUS_ONE_REQUESTS,
} from "@src/graphql/queries";
import {
  UpdateRsvpStatus,
  GetInvitationSelfiePresignedUrl,
  verifyInvitationSelfieUpload,
  REQUEST_PLUS_ONE,
} from "@src/graphql/mutations";
import { getCookie, setCookie } from "cookies-next";
import defaultInviteImage from "../../../../public/images/generalDefaultImage.png";
import html2canvas from "html2canvas";
import axios from "axios";
import { QRCodeCanvas } from "qrcode.react";
import {
  DownloadPassIcon,
  SelfiePlaceholderIcon,
  CameraIcon,
  InawoPassIcon,
} from "@src/components/Icon/Icons";
import { toPng } from "html-to-image";
import CustomQrCode from "@src/components/QrCode";
import useStore from "@src/store";

interface InvitationProps {
  name: string | null;
  date: string;
  eventId: number;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  access_control: boolean;
  eventUuid?: string;
  eventSlug?: string;
  passImage?: string;
}

type PlusOneDetails = {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
};

const plusOneDetailsSchema = Yup.object().shape({
  guests: Yup.array()
    .of(
      Yup.object().shape({
        firstName: Yup.string().required("Please fill in the first name"),
        lastName: Yup.string().required("Please fill in the last name"),
        phone: Yup.string().required("Please fill in the phone number"),
        email: Yup.string().email("Please enter a correct email address"),
      })
    )
    .min(1)
    .max(5),
});

type PlusOneFormValues = {
  guests: PlusOneDetails[];
};

const primaryButtonClass =
  "inline-flex min-h-[40px] w-full items-center justify-center rounded-xl border border-[#1C2926] bg-[#3b5f59] px-4 py-2 font-['Plain'] text-xs font-semibold uppercase tracking-[0.12em] text-[#FFFDF8] shadow-[0_10px_22px_rgba(28,41,38,0.16)] transition duration-200 hover:-translate-y-px disabled:cursor-not-allowed disabled:opacity-60";

const secondaryButtonClass =
  "inline-flex min-h-[40px] w-full items-center justify-center rounded-xl border border-[#D4BD8D] bg-transparent px-4 py-2 font-['Plain'] text-xs font-semibold uppercase tracking-[0.12em] text-[#6E542C] transition duration-200 hover:-translate-y-px hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60";

function PlusOneCountPicker({
  value,
  onChange,
  disabled = false,
}: {
  value: number;
  onChange: (count: number) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = React.useState(false);

  return (
    <div className="relative w-full text-left">
      <span className="mb-1 block text-sm font-medium text-[#5B5B5B]">
        Number of plus-ones
      </span>
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => setOpen((current) => !current)}
        className="flex w-full items-center justify-between rounded-xl border border-[#D4BD8D] bg-white px-4 py-3 text-sm font-semibold text-[#1C2926] shadow-sm transition hover:border-[#A8844C] disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span>{value}</span>
        <span aria-hidden="true" className="text-base text-[#8A6B38]">
          {open ? "⌃" : "⌄"}
        </span>
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Number of plus-ones"
          className="absolute z-20 mt-2 grid w-full grid-cols-5 gap-1 rounded-xl border border-[#D4BD8D] bg-[#FFFDF8] p-2 shadow-xl"
        >
          {[1, 2, 3, 4, 5].map((count) => (
            <button
              key={count}
              type="button"
              role="option"
              aria-selected={value === count}
              onClick={() => {
                onChange(count);
                setOpen(false);
              }}
              className={`rounded-lg py-2 text-sm font-semibold transition ${
                value === count
                  ? "bg-[#3B5F59] text-white"
                  : "text-[#5B5B5B] hover:bg-[#F1E8D7]"
              }`}
            >
              {count}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const Invitation = ({
  date,
  name,
  eventId,
  setOpen,
  access_control,
  eventUuid,
  eventSlug,
  passImage,
}: InvitationProps) => {
  const isPreviewMode = useStore((state) => state.isPreviewMode);

  const currentAccessToken = getCookie("tsl") as string;
  const [step, setStep] = React.useState<number>(1);
  const [picture, setPicture] = React.useState<File | null>(null);
  const cardRef = React.useRef<HTMLDivElement>(null);
  const [show, setShow] = React.useState<boolean>(false);
  const [activeRsvpAction, setActiveRsvpAction] = React.useState<
    "yes" | "no" | null
  >(null);
  const [activeTab, setActiveTab] = React.useState<"rsvp" | "plusOne">("rsvp");
  const [plusOneMode, setPlusOneMode] = React.useState<"choice" | "details">(
    "choice"
  );
  const [unnamedAdmits, setUnnamedAdmits] = React.useState(1);
  const [detailAdmits, setDetailAdmits] = React.useState(1);
  const [activeGuestIndex, setActiveGuestIndex] = React.useState(0);
  const [activePlusOneAction, setActivePlusOneAction] = React.useState<
    "unnamed" | "details" | null
  >(null);
  const invitationUuid = getCookie("invitationUuid") as string | undefined;
  const resolvedEventUuid =
    eventUuid ?? (getCookie("eventUuid") as string | undefined);
  const canRequestPlusOne = getCookie("invitationRsvpStatus") === "yes";

  React.useEffect(() => {
    setShow(true);
  }, []);

  const { data: invitationCardData, loading: invitationCardLoading } = useQuery<
    InvitationCardQuery,
    InvitationCardQueryVariables
  >(InvitationCard, {
    skip: !eventId,
    variables: {
      eventId,
    },
  });

  const { data: eventPreferenceData } = useQuery<
    Event_PreferencesQuery,
    Event_PreferencesQueryVariables
  >(GetEventPreferences, {
    skip: !resolvedEventUuid,
    variables: {
      eventId: resolvedEventUuid ?? "",
    },
  });

  const plusOneRequestEnabled =
    eventPreferenceData?.event_preferences?.plus_one_request === true;

  React.useEffect(() => {
    if (!plusOneRequestEnabled && activeTab === "plusOne") {
      setActiveTab("rsvp");
    }
  }, [activeTab, plusOneRequestEnabled]);

  const {
    data: plusOneRequestsData,
    loading: plusOneRequestsLoading,
    refetch: refetchPlusOneRequests,
  } = useQuery<MyPlusOneRequestsQuery, MyPlusOneRequestsQueryVariables>(
    MY_PLUS_ONE_REQUESTS,
    {
      skip: !resolvedEventUuid || !invitationUuid || !plusOneRequestEnabled,
      variables: {
        eventId: resolvedEventUuid ?? "",
        invitationId: invitationUuid ?? "",
      },
      fetchPolicy: "cache-and-network",
    }
  );

  const [UpdateRsvpStatusFn, { loading }] = useMutation<
    UpdateRsvpStatusMutation,
    UpdateRsvpStatusMutationVariables
  >(UpdateRsvpStatus, {
    onCompleted() {
      setCookie("invitationRsvpStatus", activeRsvpAction ?? "");
      // if (access_control === true) {
      //   toast.success(<p className="toast">RSVP sent successfully.</p>);
      //   setStep(2);
      // } else {
      //   setOpen(false);
      //   toast.success(<p className="toast">RSVP sent successfully.</p>);
      // }

      setActiveRsvpAction(null);
      setOpen(false);
      toast.success(<p className="toast">RSVP sent successfully.</p>);
    },
    onError(error) {
      setActiveRsvpAction(null);
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      }
    },
  });

  const [requestPlusOne, { loading: plusOneLoading }] = useMutation<
    RequestPlusOneMutation,
    RequestPlusOneMutationVariables
  >(REQUEST_PLUS_ONE, {
    onCompleted: async () => {
      setActivePlusOneAction(null);
      setPlusOneMode("choice");
      await refetchPlusOneRequests();
      toast.success(
        <p className="toast">Your plus-one request has been sent.</p>
      );
    },
    onError(error) {
      setActivePlusOneAction(null);
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">
            {error.message ?? "We couldn't send your plus-one request."}
          </p>
        );
      }
    },
  });

  const [verifySelfieUpload, { loading: verifySelfieUploadLoading }] =
    useMutation<
      VerifyInvitationSelfieUploadMutation,
      VerifyInvitationSelfieUploadMutationVariables
    >(verifyInvitationSelfieUpload);

  const [uploadSelfie, { loading: getMediaPresignedUrlLoading }] = useMutation<
    GenerateInvitationSelfiePreSignedUrlMutation,
    GenerateInvitationSelfiePreSignedUrlMutationVariables
  >(GetInvitationSelfiePresignedUrl);

  const handleMediaUpload = async () => {
    if (picture) {
      const fileExtension = picture.type.split("/")[1];
      try {
        const res = await uploadSelfie({
          variables: {
            eventId: eventUuid ?? "",
            mediaType: fileExtension,
            invitationId: eventUuid ?? "",
          },
        });

        if (res?.data?.generateInvitationSelfiePreSignedUrl?.upload_url) {
          await axios
            .put(
              res.data.generateInvitationSelfiePreSignedUrl.upload_url,
              picture,
              {
                headers: {
                  "Content-Type": fileExtension,
                  "x-amz-acl": "public-read",
                },
              }
            )
            .then(() => console.log("Upload successful"))
            .catch((error) => console.error("Axios upload error:", error));
          toast.success("Selfie uploaded successfully");

          await verifySelfieUpload({
            variables: {
              eventId: eventUuid ?? "",
              invitationId: eventUuid ?? "",
              fileUri:
                res?.data?.generateInvitationSelfiePreSignedUrl?.file_uri ?? "",
            },
          })
            .then(() => {
              toast.success("Selfie verified successfully");
              setStep(step + 1);
            })
            .catch((error) => {
              console.error("Selfie verification error:", error);
              toast.error("An error occurred while verifying selfie");
            });
        }
      } catch (error) {
        console.error("Upload error:", error);
        toast.error("An error occurred while uploading");
      }
    } else {
      toast.error("Please select a selfie picture to upload");
    }
  };

  const handleDownload = async () => {
    if (cardRef.current) {
      const dataUrl = await toPng(cardRef.current);
      const link = document.createElement("a");
      link.download = "love-card.png";
      link.href = dataUrl;
      link.click();
    }
  };

  const handleRsvpComing = () => {
    setActiveRsvpAction("yes");
    UpdateRsvpStatusFn({
      variables: {
        eventId: eventId,
        status: "yes",
      },
    });
  };

  const handleRsvpNotComing = () => {
    setActiveRsvpAction("no");
    UpdateRsvpStatusFn({
      variables: {
        eventId: eventId,
        status: "no",
      },
    });
  };

  const submitPlusOneRequest = (admits: number, guests?: PlusOneDetails[]) => {
    if (!resolvedEventUuid) {
      toast.error(
        <p className="toast">
          We couldn't identify this event. Please reload and try again.
        </p>
      );
      return;
    }

    requestPlusOne({
      variables: {
        eventId: resolvedEventUuid,
        guest: guests
          ? {
              admits,
              request_type: PlusOneRequestType.Guest,
              guests: guests.map((guest) => ({
                first_name: guest.firstName,
                last_name: guest.lastName,
                phone: guest.phone,
                email: guest.email || undefined,
              })),
            }
          : {
              admits,
              request_type: PlusOneRequestType.Admit,
              guests: [],
            },
      },
    });
  };

  const renderPlusOneTab = () => {
    if (!currentAccessToken) {
      return (
        <p className="text-center text-sm text-[#71717A]">
          Open your invitation link to request a plus-one.
        </p>
      );
    }

    return (
      <div className="flex w-full flex-col gap-5">
        {!canRequestPlusOne ? (
          <p className="rounded-lg bg-[#FFFBEB] p-3 text-center text-sm text-[#9A6700]">
            Please RSVP “I&apos;m Coming” before requesting a plus-one.
          </p>
        ) : plusOneMode === "choice" ? (
          <div className="flex flex-col items-center gap-3 text-center">
            <h3 className="font-['Tiempos_Headline'] text-xl font-normal tracking-wide text-[#1C2926]">
              Bring a plus-one
            </h3>
            <p className="text-sm text-[#71717A]">
              Add their details now, or send a request without them. The couple
              will review your request.
            </p>
            <PlusOneCountPicker
              value={unnamedAdmits}
              onChange={setUnnamedAdmits}
              disabled={plusOneLoading}
            />
            <Button
              type="button"
              handleClick={() => setPlusOneMode("details")}
              design="another"
              className={primaryButtonClass}
              label="Add plus-one details"
            >
              Add plus-one details
            </Button>
            <Button
              type="button"
              handleClick={() => {
                setActivePlusOneAction("unnamed");
                submitPlusOneRequest(unnamedAdmits);
              }}
              loading={plusOneLoading && activePlusOneAction === "unnamed"}
              disabled={isPreviewMode}
              design="secondary"
              className={secondaryButtonClass}
              label="Request without details"
            >
              Request without details
            </Button>
          </div>
        ) : (
          <Formik<PlusOneFormValues>
            enableReinitialize
            initialValues={{
              guests: Array.from({ length: detailAdmits }, () => ({
                firstName: "",
                lastName: "",
                phone: "",
                email: "",
              })),
            }}
            validationSchema={plusOneDetailsSchema}
            onSubmit={(values) => {
              setActivePlusOneAction("details");
              submitPlusOneRequest(detailAdmits, values.guests);
            }}
          >
            {(props: FormikProps<PlusOneFormValues>) => {
              const isLastGuest = activeGuestIndex === detailAdmits - 1;

              const goToNextGuest = async () => {
                const fields = ["firstName", "lastName", "phone", "email"];
                await Promise.all(
                  fields.map((field) =>
                    props.setFieldTouched(
                      `guests.${activeGuestIndex}.${field}`,
                      true,
                      false
                    )
                  )
                );
                const errors = await props.validateForm();
                const guestErrors = errors.guests?.[activeGuestIndex];
                if (!guestErrors) setActiveGuestIndex((index) => index + 1);
              };

              return (
                <Form className="flex flex-col gap-3">
                  <h3 className="text-center font-['Tiempos_Headline'] text-xl font-normal tracking-wide text-[#1C2926]">
                    Enter your plus-one&apos;s details
                  </h3>
                  <PlusOneCountPicker
                    value={detailAdmits}
                    onChange={(count) => {
                      setDetailAdmits(count);
                      setActiveGuestIndex(0);
                    }}
                    disabled={plusOneLoading}
                  />
                  <div className="flex items-center justify-between rounded-xl bg-[#F8F3EA] px-4 py-3 text-sm font-semibold text-[#5B5B5B]">
                    <span>
                      Plus-one {activeGuestIndex + 1} of {detailAdmits}
                    </span>
                    <span className="flex gap-1" aria-hidden="true">
                      {Array.from({ length: detailAdmits }, (_, index) => (
                        <i
                          key={index}
                          className={`block h-2 rounded-full ${
                            index === activeGuestIndex
                              ? "w-5 bg-[#3B5F59]"
                              : "w-2 bg-[#D4BD8D]"
                          }`}
                        />
                      ))}
                    </span>
                  </div>
                  <Input
                    type="text"
                    name={`guests.${activeGuestIndex}.firstName`}
                    label="First name"
                    placeholder="Enter first name"
                  />
                  <Input
                    type="text"
                    name={`guests.${activeGuestIndex}.lastName`}
                    label="Last name"
                    placeholder="Enter last name"
                  />
                  <PhoneNumberInput
                    name={`guests.${activeGuestIndex}.phone`}
                    label="Phone number"
                    placeholder="Enter phone number"
                    value={props.values.guests[activeGuestIndex]?.phone ?? ""}
                    isError={Boolean(
                      getIn(
                        props.touched,
                        `guests.${activeGuestIndex}.phone`
                      ) &&
                        getIn(props.errors, `guests.${activeGuestIndex}.phone`)
                    )}
                    setValue={(value) => {
                      const currentValue =
                        props.values.guests[activeGuestIndex]?.phone ?? "";
                      const nextValue =
                        typeof value === "function"
                          ? value(currentValue)
                          : value;

                      void props.setFieldValue(
                        `guests.${activeGuestIndex}.phone`,
                        nextValue
                      );
                      void props.setFieldTouched(
                        `guests.${activeGuestIndex}.phone`,
                        true,
                        false
                      );
                    }}
                  />
                  <Input
                    type="email"
                    name={`guests.${activeGuestIndex}.email`}
                    label="Email (optional)"
                    placeholder="Enter email"
                  />
                  <div className="flex items-center justify-between gap-3">
                    <Button
                      type="button"
                      handleClick={() =>
                        setActiveGuestIndex((index) => index - 1)
                      }
                      disabled={plusOneLoading || activeGuestIndex === 0}
                      design="secondary"
                      className="inline-flex min-h-[40px] flex-1 items-center justify-center rounded-xl border border-[#D4BD8D] bg-white px-4 py-3 font-['Plain'] text-xs font-semibold uppercase tracking-[0.12em] text-[#6E542C] disabled:cursor-not-allowed disabled:opacity-50"
                      label="Previous"
                    >
                      Previous
                    </Button>
                    {isLastGuest ? (
                      <Button
                        type="submit"
                        loading={
                          plusOneLoading && activePlusOneAction === "details"
                        }
                        disabled={isPreviewMode}
                        design="another"
                        className="inline-flex min-h-[40px] flex-1 items-center justify-center rounded-xl border border-[#1C2926] bg-[#3B5F59] px-4 py-3 font-['Plain'] text-xs font-semibold uppercase tracking-[0.12em] text-[#FFFDF8] disabled:cursor-not-allowed disabled:opacity-60"
                        label="Send request with details"
                      >
                        Send request
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        handleClick={goToNextGuest}
                        disabled={plusOneLoading}
                        design="another"
                        className="inline-flex min-h-[40px] flex-1 items-center justify-center rounded-xl border border-[#1C2926] bg-[#3B5F59] px-4 py-3 font-['Plain'] text-xs font-semibold uppercase tracking-[0.12em] text-[#FFFDF8] disabled:cursor-not-allowed disabled:opacity-60"
                        label="Next"
                      >
                        Next
                      </Button>
                    )}
                  </div>
                  <Button
                    type="button"
                    handleClick={() => {
                      props.resetForm();
                      setActiveGuestIndex(0);
                      setPlusOneMode("choice");
                    }}
                    disabled={plusOneLoading}
                    design="secondary"
                    className={secondaryButtonClass}
                    label="Back"
                  >
                    Back
                  </Button>
                </Form>
              );
            }}
          </Formik>
        )}

        <div className="border-t border-[#E4E4E7] pt-4">
          <h4 className="mb-3 font-['Tiempos_Headline'] text-lg font-normal tracking-wide text-[#1C2926]">
            Your requests
          </h4>
          {plusOneRequestsLoading ? (
            <p className="text-sm text-[#71717A]">Loading requests…</p>
          ) : (plusOneRequestsData?.myPlusOneRequests.length ?? 0) === 0 ? (
            <p className="text-sm text-[#71717A]">
              You have not sent any plus-one requests.
            </p>
          ) : (
            <div className="flex flex-col gap-2">
              {plusOneRequestsData?.myPlusOneRequests.map((request) => {
                const name = [request.guest_first_name, request.guest_last_name]
                  .filter(Boolean)
                  .join(" ");
                const status = request.status.toLowerCase();
                const statusClass =
                  status === "accepted"
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-600/20"
                    : status === "declined" || status === "rejected"
                    ? "bg-rose-50 text-rose-700 ring-rose-600/20"
                    : status === "pending"
                    ? "bg-amber-50 text-amber-700 ring-amber-600/20"
                    : "bg-slate-50 text-slate-700 ring-slate-600/20";
                return (
                  <div
                    key={request.id}
                    className="rounded-lg border border-[#E4E4E7] p-3"
                  >
                    <div className="flex items-center justify-between gap-3">
                      <p className="font-medium">
                        {name || "Unnamed plus-one"}
                      </p>
                      <span
                        className={`rounded-full px-2.5 py-1 text-xs font-semibold capitalize ring-1 ring-inset ${statusClass}`}
                      >
                        {status}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-[#71717A]">
                      {request.guest_phone ||
                        request.guest_email ||
                        "No contact details"}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {
        //   access_control ? (
        //   <div className={invitation["invitation--content"]}>
        //     {isPreviewMode && (
        //       <p className="text-sm text-center text-[#71717A] mb-4">
        //         You are in preview mode. The seating information is not real and
        //         is for demonstration purposes only.
        //       </p>
        //     )}
        //     {step === 1 ? (
        //       <>
        //         {invitationCardLoading ? (
        //           <div
        //             className={`${invitation["invitation--content__image"]} ${invitation["invitation--content__image--loading"]}`}
        //           >
        //             Loading...
        //           </div>
        //         ) : !!invitationCardData?.invitationCard?.static_image ? (
        //           <div className={invitation["invitation--content__image"]}>
        //             <Image
        //               src={invitationCardData?.invitationCard?.static_image}
        //               layout="fill"
        //               alt="Invitation card"
        //               objectFit="contain"
        //             />
        //           </div>
        //         ) : (
        //           <div className={invitation["invitation--content__image"]}>
        //             <Image
        //               src={defaultInviteImage}
        //               layout="fill"
        //               alt="Invitation card"
        //               objectFit="contain"
        //             />
        //           </div>
        //         )}
        //       </>
        //     ) : step === 2 ? (
        //       <div className="flex flex-col gap-[40px] items-center w-full lg:w-[470px] mx-auto">
        //         <div className="flex flex-col gap-[12px] items-center">
        //           <h3 className="font-medium text-[26px]">We need a selfie</h3>
        //           <p className="text-[#71717A] text-[14px]">
        //             This is for verification purposes during the wedding
        //           </p>
        //         </div>
        //         <div className="flex flex-col gap-[16px] items-center">
        //           {picture ? (
        //             <img
        //               src={URL.createObjectURL(picture)}
        //               alt="user picture"
        //               className="w-[164px] h-[224px] rounded-[56px] object-cover"
        //               style={{ border: "1px solid #71717A" }}
        //             />
        //           ) : (
        //             <SelfiePlaceholderIcon />
        //           )}
        //           <label
        //             htmlFor="upload"
        //             className="flex items-center gap-[8px] rounded-[8px] py-[8px] px-[12px] font-medium shadow-lg"
        //           >
        //             <input
        //               type="file"
        //               accept="image/*"
        //               onChange={(e) => {
        //                 if (e.target.files && e.target.files[0]) {
        //                   setPicture(e.target.files[0]);
        //                 }
        //               }}
        //               className="hidden"
        //               id="upload"
        //             />
        //             <CameraIcon />
        //             <p>
        //               {picture ? "Change selfie image" : "Select selfie image"}
        //             </p>
        //           </label>
        //         </div>
        //         <div className="w-full">
        //           <button
        //             onClick={handleMediaUpload}
        //             style={{ border: "1px solid #e8b13d" }}
        //             className="w-full mb-[8px] rounded-[8px] flex items-center justify-center p-[16px] text-[14px] text-center"
        //           >
        //             {getMediaPresignedUrlLoading || verifySelfieUploadLoading
        //               ? "uploading selfie..."
        //               : "Continue"}
        //           </button>
        //           <p className="text-[#71717A] text-[14px] text-center">
        //             Works better on your mobile device
        //           </p>
        //         </div>
        //       </div>
        //     ) : (
        //       <div className="flex flex-col gap-[40px] items-center w-full lg:w-[470px] mx-auto">
        //         <div className="flex flex-col gap-[12px] items-center">
        //           <h3 className="font-medium text-[26px]">Your pass</h3>
        //           <p className="text-[#71717A] text-[14px]">
        //             Show this at the venue
        //           </p>
        //         </div>
        //         <div className="flex flex-col gap-[16px] items-center">
        //           {/* <div className="w-[188px] mx-auto h-[300px] rounded-[22px] flex items-center justify-center flex-col gap-[20px] px-[50px] bg-gradient-to-br from-[#026051] via-[#47A092CC] to-[#3B5F5900]"> */}
        //           <div
        //             className="w-[188px] shadow-md h-[300px] rounded-[22px] py-[9px] px-[15px] border border-[#D9D9D9] flex flex-col items-center justify-between"
        //             style={{
        //               backgroundImage: `url(${passImage})`,
        //               backgroundRepeat: "no-repeat",
        //               backgroundSize: "cover",
        //               backgroundPosition: "center",
        //             }}
        //             ref={cardRef}
        //           >
        //             <p className="my-[11px] text-[11px] font-medium text-white">
        //               #{eventSlug}
        //             </p>
        //             <div
        //               className="w-full rounded-[22px] bg-[#FFFFFFCC] h-[211px] px-[10px] py-[10px] flex flex-col items-center justify-between"
        //               style={{ backdropFilter: "blur(3px)" }}
        //             >
        //               <div>
        //                 <p className="text-[9px] text-[#4D4D4DE8] mb-[2px]">
        //                   Guest code
        //                 </p>
        //                 <p className="text-[10px] text-[#4D4D4D4D]">
        //                   {"TWE2110"}
        //                 </p>
        //               </div>
        //               <div className="w-[105px] h-[105px] border border-[#3D516080] rounded-[20px] bg-gradient-to-b from-[#E6FA5200] to-[#E6FA5233] p-[20px]">
        //                 <QRCodeCanvas
        //                   value="TWE2110"
        //                   fgColor="#4F7770"
        //                   size={65}
        //                 />
        //               </div>
        //               <div className="flex flex-col items-center justify-center">
        //                 <p className="mb-[2px] text-[#4D4D4D99] text-[10px] text-center">
        //                   This is your pass to the wedding
        //                 </p>
        //                 <InawoPassIcon />
        //               </div>
        //             </div>
        //           </div>
        //           {/* </div> */}
        //           <button
        //             onClick={handleDownload}
        //             className="flex items-center justify-center gap-[8px] bg-[#FFFBEB] w-[188px] mx-auto h-[35px] rounded-[7px] py-[7px] px-[12px] font-medium shadow-lg"
        //             style={{ border: "0.579px solid #5C5C5C" }}
        //           >
        //             <DownloadPassIcon />
        //             <p className="text-[#141414] text-[12px] font-normal">
        //               Download your pass
        //             </p>
        //           </button>
        //         </div>
        //         <div className="w-full">
        //           <button
        //             onClick={() => setStep(6)}
        //             style={{ border: "1px solid #e8b13d" }}
        //             className="w-full mb-[8px] rounded-[8px] p-[16px] text-[14px] text-center"
        //           >
        //             Continue
        //           </button>
        //           <p className="text-[#71717A] text-[14px] text-center">
        //             This pass has been sent to your mail
        //           </p>
        //         </div>
        //       </div>
        //     )}

        //     {show && !!currentAccessToken && (
        //       <div className={invitation["invitation--content__bottom"]}>
        //         <div>
        //           <Button
        //             loading={loading}
        //             handleClick={() =>
        //               UpdateRsvpStatusFn({
        //                 variables: {
        //                   eventId: eventId,
        //                   status: "yes",
        //                 },
        //               })
        //             }
        //             type="button"
        //             design="another"
        //             label="I'm Coming"
        //             disabled={isPreviewMode}
        //           >
        //             I&apos;m Coming
        //           </Button>
        //         </div>
        //         <div>
        //           <Button
        //             loading={loading}
        //             handleClick={() =>
        //               UpdateRsvpStatusFn({
        //                 variables: {
        //                   eventId: eventId,
        //                   status: "no",
        //                 },
        //               })
        //             }
        //             type="button"
        //             design="secondary"
        //             label="I'm Not Coming"
        //             disabled={isPreviewMode}
        //           >
        //             I&apos;m Not Coming
        //           </Button>
        //         </div>
        //       </div>
        //     )}
        //   </div>
        // ) :
        <div
          className={`${invitation["invitation--content"]} rounded-[20px] border border-[#C8A46A]/30 bg-[linear-gradient(145deg,#FFFDF8_0%,#FFFFFF_58%,#FAF7F0_100%)] font-['Plain']`}
        >
          {isPreviewMode && (
            <p className="text-sm text-center text-[#71717A] mb-4">
              You are in preview mode. This is for demonstration purposes only.
            </p>
          )}
          <div className="flex w-full border-b border-[#E4E4E7]">
            <button
              type="button"
              onClick={() => setActiveTab("rsvp")}
              className={`flex-1 border-b-2 px-3 py-3 font-['Plain'] text-xs font-semibold uppercase tracking-[0.15em] transition ${
                activeTab === "rsvp"
                  ? "border-[#C8A46A] text-[#1C2926]"
                  : "border-transparent text-[#85827C] hover:text-[#1C2926]"
              }`}
            >
              RSVP
            </button>
            {plusOneRequestEnabled && (
              <button
                type="button"
                onClick={() => setActiveTab("plusOne")}
                className={`flex-1 border-b-2 px-3 py-3 font-['Plain'] text-xs font-semibold uppercase tracking-[0.15em] transition ${
                  activeTab === "plusOne"
                    ? "border-[#C8A46A] text-[#1C2926]"
                    : "border-transparent text-[#85827C] hover:text-[#1C2926]"
                }`}
              >
                Plus One
              </button>
            )}
          </div>
          {activeTab === "rsvp" ? (
            <>
              {invitationCardLoading ? (
                <div
                  className={`${invitation["invitation--content__image"]} ${invitation["invitation--content__image--loading"]}`}
                >
                  Loading...
                </div>
              ) : !!invitationCardData?.invitationCard?.static_image ? (
                <div className={invitation["invitation--content__image"]}>
                  <Image
                    src={invitationCardData?.invitationCard?.static_image}
                    layout="fill"
                    alt="Invitation card"
                    objectFit="contain"
                  />
                </div>
              ) : (
                <div className={invitation["invitation--content__image"]}>
                  <Image
                    src={defaultInviteImage}
                    layout="fill"
                    alt="Invitation card"
                    objectFit="contain"
                  />
                </div>
              )}

              {show && !!currentAccessToken && (
                <div className={invitation["invitation--content__bottom"]}>
                  <div>
                    <Button
                      loading={loading && activeRsvpAction === "yes"}
                      handleClick={handleRsvpComing}
                      type="button"
                      design="another"
                      className={primaryButtonClass}
                      label="I'm Coming"
                      disabled={isPreviewMode}
                    >
                      I&apos;m Coming
                    </Button>
                  </div>
                  <div>
                    <Button
                      loading={loading && activeRsvpAction === "no"}
                      handleClick={handleRsvpNotComing}
                      type="button"
                      design="secondary"
                      className={secondaryButtonClass}
                      label="I'm Not Coming"
                      disabled={isPreviewMode}
                    >
                      I&apos;m Not Coming
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : plusOneRequestEnabled ? (
            renderPlusOneTab()
          ) : null}
        </div>
      }
    </div>
  );
};

export default Invitation;
